import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';

import { PrimaryButton, TextButton } from '@components/Button/variants';
import TextField from '@components/Form/TextField';
import TIModal from '@components/TIModal';
import useConfirmActionViaEmailCode from '@hooks/useConfirmActionViaEmailCode';
import useInitConfirmActionViaEmailCode from '@hooks/useInitConfirmActionViaEmailCode';

/* 
  Use this when requiring email confirmation from the user before completing an action. 
  See GiftForm for implementation example.
*/

interface ConfirmActionViaEmailCodeParams {
  actionText: string;
  isOpen: boolean;
  setIsOpen: (boolean) => void;
  setIsConfirmed: (boolean) => void;
}

const RESEND_SECONDS = 60;

const INITIAL_STATE = {
  challengeCode: '',
};

const schema = () =>
  yup.object().shape({
    challengeCode: yup
      .string()
      .matches(/^\d{6}$/, 'You need to enter the 6-digit code sent to your email')
      .required('You need to enter the numeric code sent to your email'),
  });

const ConfirmActionViaEmailCode = ({
  isOpen,
  setIsOpen,
  setIsConfirmed,
  actionText,
}: ConfirmActionViaEmailCodeParams) => {
  const [invalidCodeSubmitted, setInvalidCodeSubmitted] = useState(false);
  const [canSend, setCanSend] = useState(true);
  const [remaining, setRemaining] = useState(0);

  const startCountdown = () => {
    setRemaining(RESEND_SECONDS);
  };

  useEffect(() => {
    if (remaining <= 0) {
      setCanSend(true);
      return;
    }

    const interval = setInterval(() => {
      setRemaining((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [remaining]);

  const {
    mutateAsync: initConfirmMutate,
    data: initConfirmData,
    isLoading: initConfirmIsLoading,
    isError: initConfirmIsError,
    error: initConfirmError,
  } = useInitConfirmActionViaEmailCode();
  const {
    mutate: confirmMutate,
    data: confirmData,
    isLoading: confirmIsLoading,
    isError: confirmIsError,
    error: confirmError,
  } = useConfirmActionViaEmailCode({
    onSuccess: (data) => {
      if (data) {
        setIsConfirmed(data.success);
        setInvalidCodeSubmitted(!data.success);
        setIsOpen(!data.success);
      }
    },
  });

  useEffect(() => {
    resetState();
    if (isOpen) {
      sendCode();
    }
  }, [isOpen]);

  const sendCode = () => {
    initConfirmMutate({ actionText });
    resetState();
    setCanSend(false);
    startCountdown();
  };

  const resetState = () => {
    setIsConfirmed(false);
    setInvalidCodeSubmitted(false);
  };

  const handleSubmit = (values) => {
    setInvalidCodeSubmitted(false);
    confirmMutate({ actionText: actionText, challengeCode: values.challengeCode });
  };

  if (!isOpen) return null;
  return (
    <TIModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      customHeader={false}
      customHeight={true}
      parentClass="w-1/3 h-369 px-16"
    >
      <h3>Confirm {actionText}</h3>
      {(initConfirmIsError || confirmIsError) && (
        <div className="text-red">
          Error: {JSON.stringify(initConfirmError)} {JSON.stringify(confirmError)}
        </div>
      )}

      <div>
        {!initConfirmData || initConfirmIsLoading ? (
          <div className="h-90 py-24">
            We are sending you a confirm code to {currentUser.email}...
          </div>
        ) : (
          <>
            <div className="pb-12">
              <div className="mt-4">
                We just sent a 6 digit confirmation code to {currentUser.email}
                <div className="mt-4 h-36">
                  {invalidCodeSubmitted && (
                    <span className="text-disclosure-md text-primary-magenta">
                      Sorry, that code doesn&apos;t match. Please double check the code and
                      resubmit.
                    </span>
                  )}
                  {confirmData?.success && <span className="text-green-600">Validated!</span>}
                </div>
              </div>

              <Formik
                enableReinitialize
                initialValues={INITIAL_STATE}
                onSubmit={handleSubmit}
                validationSchema={schema}
                validateOnBlur={false}
              >
                {(formik) => (
                  <Form noValidate onSubmit={formik.handleSubmit}>
                    <div className="mb-8">
                      <label className="text-disclosure-md">
                        Enter the 6-digit code from your email
                      </label>
                    </div>

                    <div className="mb-16">
                      <TextField
                        name="challengeCode"
                        autoComplete="off"
                        placeholder="Enter the code that we just sent you"
                      />
                    </div>

                    <PrimaryButton
                      as="button"
                      type="submit"
                      disabled={!(formik.isValid && formik.dirty) || confirmIsLoading}
                      isLoading={confirmIsLoading}
                      fullWidth
                    >
                      Confirm
                    </PrimaryButton>
                  </Form>
                )}
              </Formik>
            </div>
            <div className="flex items-baseline py-16 text-disclosure-md">
              Didn&apos;t get the code?{' '}
              {canSend ? (
                <TextButton as="button" onClick={sendCode} className="text-disclosure-md">
                  Resend
                </TextButton>
              ) : (
                <span className="pl-2">
                  You can resend in{' '}
                  <span className="inline-block w-16 text-center">{remaining}</span> seconds.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </TIModal>
  );
};

export default ConfirmActionViaEmailCode;
